import { useRef, useCallback, useEffect, type RefObject } from "react";
import { drawBox, drawArrow, drawPath } from "@/lib/canvas-utils";

export interface DrawingBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  opacity: number;
  strokeWidth: number;
  label?: number;
  type?: 'box' | 'arrow' | 'freedraw';
  points?: { x: number; y: number }[];
}

interface DrawingOverlayHook {
  canvasRef: RefObject<HTMLCanvasElement>;
  redraw: () => void;
}

export function useDrawingOverlay(
  imageToViewportCoords: (imageX: number, imageY: number) => { x: number; y: number },
  boxes: DrawingBox[],
  defaultColor: string
): DrawingOverlayHook {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    
    if (!canvas || !ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw all shapes using the document's default color
    boxes.forEach(box => {
      const type = box.type || 'box'; // Default to box for backward compatibility
      
      if (type === 'freedraw' && box.points && box.points.length > 0) {
        // Draw free-form path
        const viewportPoints = box.points.map(p => imageToViewportCoords(p.x, p.y));
        drawPath(ctx, { points: viewportPoints }, defaultColor, box.strokeWidth);
      } else if (type === 'arrow') {
        // Draw arrow
        const viewportStart = imageToViewportCoords(box.startX, box.startY);
        const viewportEnd = imageToViewportCoords(box.endX, box.endY);
        drawArrow(ctx, {
          startX: viewportStart.x,
          startY: viewportStart.y,
          endX: viewportEnd.x,
          endY: viewportEnd.y
        }, defaultColor, box.strokeWidth);
      } else {
        // Draw box (default)
        const viewportStart = imageToViewportCoords(box.startX, box.startY);
        const viewportEnd = imageToViewportCoords(box.endX, box.endY);
        
        const viewportBox = {
          startX: viewportStart.x,
          startY: viewportStart.y,
          endX: viewportEnd.x,
          endY: viewportEnd.y
        };
        
        drawBox(ctx, viewportBox, defaultColor, box.opacity, box.strokeWidth, box.label);
      }
    });
  }, [imageToViewportCoords, boxes, defaultColor]);
  
  // Redraw when dependencies change
  useEffect(() => {
    redraw();
  }, [redraw]);
  
  return {
    canvasRef,
    redraw
  };
}
